/**
 * The Explore tutorial: a short scripted walkthrough that teaches the four workspace verbs —
 * changing a row, naming a row, pivoting the arrangement, and copying the whole ratio — before any
 * ratio is asked for. Tutorial arm only.
 *
 * Why a mode rather than an achievement: `ProblemTreePanel` derives its tree structure and its
 * badge-index mapping from module-level constants built off `ACHIEVEMENT_METADATA`, so an extra
 * achievement would appear in *both* arms' badge trees. The tutorial must be invisible to the
 * `no-tutorial` arm, so it lives outside the achievement graph entirely.
 *
 * ## Why the stages look the way they do
 *
 * A student cannot skip this to reach the goal tree, so a stage that strands someone is worse
 * than no tutorial at all. Three rules follow from that:
 *
 * 1. **Never teach a gesture the UI cannot show.** The "Remove Multiple" button only exists on
 *    hover, and hover never fires on a touchscreen, so the multiples chapter teaches adding a
 *    copy and stops there. Teaching removal would strand every tablet student on the last stage.
 * 2. **An exact target is only safe when both directions are reachable.** Block counts use `===`
 *    because a student who overshoots can click the remove edge back down. The multiplier uses
 *    `>=` because the way back down is that same hover-only button.
 * 3. **Unaided stages get rescued, not endured.** The one stage that deliberately shows no target
 *    hands back a hint once the student goes quiet, and that hint is computed from where they
 *    actually are, so it points at the gap rather than in a fixed direction.
 * 4. **Accept anything the student could reasonably mean.** The naming stage takes any name at
 *    all rather than a word the prompt asked for. Checking spelling would turn the one stage with
 *    a keyboard in it into the one stage a student can fail.
 *
 * The stage targets below are written against `BlockBuilder`'s initial local state
 * (Value A = 3 blocks, Value B = 2, parallel, 1 copy). Stage 1 deliberately asks for a count the
 * student does not already have, so the tutorial cannot be satisfied by doing nothing.
 */

import type { ManipulativeState } from '../MathEducationProvider';

/** How long a student may sit on an unaided stage before its rescue hint appears. */
export const TUTORIAL_RESCUE_DELAY_MS = 7000;

/** A block's add ('right') or remove ('left') edge. */
export interface BlockEdgePulse {
  kind: 'block-edge';
  lineIndex: number;
  side: 'left' | 'right';
}

/**
 * One of the round workspace buttons that sits outside the block rows — the pivot button and the
 * multiplier button. They are siblings of the block rows rather than children, so they cannot be
 * reached through the same prop chain a block edge uses.
 */
export interface ControlPulse {
  kind: 'control';
  control: 'arrangement' | 'multiplier';
}

/**
 * A row's editable name. Lives in the labels column, which is a sibling of the block rows, so it
 * is reached through neither the block-edge prop chain nor the control buttons.
 */
export interface LabelPulse {
  kind: 'label';
  lineIndex: number;
}

/** What a stage highlights, so the student has a target before they know the verb. */
export type TutorialPulseTarget = BlockEdgePulse | ControlPulse | LabelPulse;

/** One verb's worth of stages. Drives the header's progress dots. */
export type TutorialChapter = 'blocks' | 'labels' | 'sequence' | 'multiples';

export interface TutorialStage {
  id: string;
  chapter: TutorialChapter;
  /** Instruction shown in place of the achievement goal header. */
  prompt: string;
  /** Highlighted from the moment the stage opens, or null to let the student aim unaided. */
  pulse: TutorialPulseTarget | null;
  /**
   * Hint for an unaided stage, shown once the student has gone quiet for
   * `TUTORIAL_RESCUE_DELAY_MS`. Only consulted when `pulse` is null — a stage already showing a
   * target has nothing to rescue.
   */
  rescuePulse?: (state: ManipulativeState) => TutorialPulseTarget | null;
  isComplete: (state: ManipulativeState) => boolean;
}

/** The row the whole blocks chapter is about. */
const TOP_ROW = 0;

const topRowBlocks = (state: ManipulativeState): number | undefined => state.lines[TOP_ROW]?.blocks;

const topRowLabel = (state: ManipulativeState): string | undefined => state.lines[TOP_ROW]?.label;

/** The count the unaided blocks stage asks for. Named because the rescue hint compares against it. */
const UNAIDED_TARGET = 5;

/**
 * The auto-generated names a row is born with — "Value A", "Value B", and on through the alphabet
 * as `BlockBuilder.handleAddLine` creates them.
 */
const FACTORY_LABEL = /^value [a-z]$/i;

/**
 * Whether the student has given a row a name of their own. Any name counts: the stage is teaching
 * that rows *can* be named, and a check against a particular word would make the lesson about
 * spelling. Still has to exclude the factory names, or the stage would clear before it opened.
 */
const isNamed = (label: string | undefined): boolean => {
  const trimmed = label?.trim() ?? '';
  return trimmed.length > 0 && !FACTORY_LABEL.test(trimmed);
};

export const TUTORIAL_STAGES: readonly TutorialStage[] = [
  // Chapter 1 — the blocks. The prompts name the target number but not the gesture: the pulsing
  // edge shows where to click, so spelling the gesture out in words is noise to read past. The
  // target itself has to be stated, because the check is an exact count.
  {
    id: 'add',
    chapter: 'blocks',
    prompt: 'Add one block. Make the top row 4.',
    pulse: { kind: 'block-edge', lineIndex: TOP_ROW, side: 'right' },
    isComplete: state => topRowBlocks(state) === 4,
  },
  {
    id: 'remove',
    chapter: 'blocks',
    prompt: 'Now take one away. Make it 3.',
    pulse: { kind: 'block-edge', lineIndex: TOP_ROW, side: 'left' },
    isComplete: state => topRowBlocks(state) === 3,
  },
  {
    id: 'free',
    chapter: 'blocks',
    prompt: 'Your turn: make the top row 5.',
    pulse: null,
    rescuePulse: state => {
      const blocks = topRowBlocks(state);
      if (blocks === undefined || blocks === UNAIDED_TARGET) {
        return null;
      }
      // Point at whichever edge closes the gap. A student who overshot to 8 needs the remove
      // edge; pointing them at "add" again would be worse than showing nothing.
      return { kind: 'block-edge', lineIndex: TOP_ROW, side: blocks < UNAIDED_TARGET ? 'right' : 'left' };
    },
    isComplete: state => topRowBlocks(state) === UNAIDED_TARGET,
  },

  // Chapter 2 — the names. Placed here, straight after the student has built a row of five, so
  // the question "five what?" is already in the air. It also pays off in the next chapter: the
  // names travel with the rows through the pivot, which is what makes it read as the same values
  // seen differently rather than a new screen.
  //
  // The only stage completed by typing rather than clicking. Nothing arrives until the student
  // commits with Enter or clicks away, so the stage sits unfinished while the editor is open —
  // correctly, since a half-typed name is not yet a name.
  {
    id: 'rename',
    chapter: 'labels',
    prompt: 'Blocks can stand for anything. Click the top label and name it.',
    pulse: { kind: 'label', lineIndex: TOP_ROW },
    isComplete: state => isNamed(topRowLabel(state)),
  },

  // Chapter 3 — the pivot. Both stages stay highlighted: there is no aim to practise, and the
  // second one exists purely to prove the first was reversible. A student who pivots, panics,
  // and cannot find their rows again has lost more than the stage is worth.
  {
    id: 'pivot',
    chapter: 'sequence',
    prompt: 'There is another way to see this. Line the values up end to end.',
    pulse: { kind: 'control', control: 'arrangement' },
    isComplete: state => state.arrangement === 'sequence',
  },
  {
    id: 'pivot-back',
    chapter: 'sequence',
    // Also leaves the workspace in the parallel arrangement, so the multiples chapter starts from
    // a known state rather than from wherever the student happened to stop.
    prompt: 'Same blocks, just a different view. Put them back into rows.',
    pulse: { kind: 'control', control: 'arrangement' },
    isComplete: state => state.arrangement === 'parallel',
  },

  // Chapter 4 — multiples. One stage, not two: the button is labelled with the count it will
  // produce, so a second unaided click would be busywork rather than practice.
  {
    id: 'multiply',
    chapter: 'multiples',
    prompt: 'Last one: make a second copy of the whole thing.',
    pulse: { kind: 'control', control: 'multiplier' },
    isComplete: state => state.cloneMultiplier >= 2,
  },
];

/** Chapters in teaching order, derived from the stages so the two cannot drift apart. */
export const TUTORIAL_CHAPTERS: readonly TutorialChapter[] = [...new Set(TUTORIAL_STAGES.map(stage => stage.chapter))];

/**
 * True once every stage is cleared. At this point the student has learned the verbs and the
 * tutorial shows its final beat — being handed the goal tree.
 */
export const isTutorialComplete = (stageIndex: number): boolean => stageIndex >= TUTORIAL_STAGES.length;

export const getTutorialStage = (stageIndex: number): TutorialStage | null =>
  isTutorialComplete(stageIndex) || stageIndex < 0 ? null : TUTORIAL_STAGES[stageIndex];

/** The chapter being taught right now, or null once the tutorial is finished. */
export const getCurrentChapter = (stageIndex: number): TutorialChapter | null =>
  getTutorialStage(stageIndex)?.chapter ?? null;

/** True once every stage belonging to `chapter` sits behind the current position. */
export const isChapterComplete = (chapter: TutorialChapter, stageIndex: number): boolean =>
  TUTORIAL_STAGES.every((stage, index) => stage.chapter !== chapter || index < stageIndex);

/**
 * Advance past the current stage when its target is met, otherwise return the index unchanged.
 * Pure and idempotent, so it is safe to call on every render.
 */
export const advanceTutorialStage = (stageIndex: number, state: ManipulativeState): number => {
  const stage = getTutorialStage(stageIndex);
  return stage && stage.isComplete(state) ? stageIndex + 1 : stageIndex;
};

/**
 * What to highlight right now. A stage with a fixed target shows it immediately; an unaided stage
 * shows nothing until `isIdle`, at which point its rescue hint takes over.
 */
export const getTutorialPulse = (
  stageIndex: number,
  state: ManipulativeState,
  isIdle: boolean,
): TutorialPulseTarget | null => {
  const stage = getTutorialStage(stageIndex);
  if (!stage) {
    return null;
  }
  if (stage.pulse) {
    return stage.pulse;
  }
  return isIdle ? stage.rescuePulse?.(state) ?? null : null;
};
